import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Declaraciones globales del tag gtag.js cargado en index.html.
 * Se declara como opcional para poder validar antes de llamar.
 */
declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Parámetros base que acompañan a TODO evento GA4 del wayfinding.
 * Permiten segmentar en GA4 por totem, edificio y canal sin repetir código.
 */
interface BaseEventParams {
  totem_id?: number | string;
  building_id?: number | string;
  channel?: number | string;
  language?: string;
  session_id?: string;
}

/**
 * Parámetros específicos de cada tipo de evento de negocio.
 * Tipar los eventos evita errores de nombre y facilita la trazabilidad.
 */
interface SearchStoreParams extends BaseEventParams {
  search_term: string;
  search_category: 'tiendas' | 'servicios' | 'eventos' | string;
  results_count: number;
  zero_results: boolean;
}

interface RouteGeneratedParams extends BaseEventParams {
  origin_totem: string;
  origin_type: 'Totem' | 'tiendas' | 'servicios' | string;
  destination_id: number | string;
  destination_name: string;
  destination_type: 'tiendas' | 'servicios' | 'estacionamiento' | string;
  transport_mode?: 'pie' | 'alternativo' | string;
}

interface QrSendMobileParams extends BaseEventParams {
  qr_id: string;
  destination_id?: number | string;
  destination_name?: string;
  method: 'qr' | 'link' | 'sms';
}

interface QrScanEntryParams extends BaseEventParams {
  /** Nombre humano del QR, p.ej. "P2QR3". */
  qr_name?: string;
  /** Alias retrocompatible con callers antiguos. Equivalente a qr_name. */
  totem_name?: string;
  /** Id numerico del totem (tot_id) cuando se puede resolver desde el catalogo. */
  totem_id?: number | string;
  /** Piso asociado al QR. */
  floor?: string;
  /** Hash / codigo cifrado del QR. */
  code?: string;
  /** Alias retrocompatible para el hash. */
  qr_code?: string;
}

/**
 * Servicio único de tracking GA4 para el wayfinding.
 *
 * Mejoras respecto a la versión anterior:
 *  - Un único servicio (se elimina google-analytics.service.ts legacy).
 *  - Comprobación defensiva de gtag y del measurement ID.
 *  - Parámetros base comunes (totem, edificio, canal) anexados a todos los eventos.
 *  - page_view automático en cada NavigationEnd del Router.
 *  - session_id persistido en sessionStorage para agrupar flujos del mismo usuario.
 *  - Debug mode y dry-run controlados desde environment.
 *  - Consent Mode v2 (ad_storage/analytics_storage) por defecto "denied" en producción
 *    y se promueve a "granted" solo cuando se ha aceptado o no aplica cookie-banner.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private sessionId: string;
  private initialized = false;

  constructor(private router: Router) {
    this.sessionId = this.ensureSessionId();
    this.initRouterTracking();
  }

  // ---------------------------------------------------------------------------
  // Inicialización y helpers privados
  // ---------------------------------------------------------------------------

  /**
   * Escucha el router y dispara un page_view por cada NavigationEnd.
   * Esto soluciona el problema típico de SPA donde GA4 solo ve el primer load.
   */
  private initRouterTracking(): void {
    if (this.initialized) { return; }
    this.initialized = true;

    this.router.events
      .pipe(filter(ev => ev instanceof NavigationEnd))
      .subscribe((ev: NavigationEnd) => {
        const url = ev.urlAfterRedirects || ev.url;

        // Rutas suprimidas: las registra el propio componente con un titulo
        // especifico ("QR: P2QR3", "Tienda: BATA", "Ruta a BATA", etc.) y no
        // queremos que el auto-tracker genere ademas un page_view generico
        // ("Mapa / ruta", "Detalle de tienda") que ensucia el reporte de
        // "Vistas por titulo de pagina" en GA4.
        if (url) {
          const suprimidos = [
            '/qrcode/',
            '/wayfinding/',
            '/mapa/',
            '/tienda-detalle/',
            '/evento-detalle/'
          ];
          for (const prefijo of suprimidos) {
            if (url.indexOf(prefijo) === 0) {
              return;
            }
          }
          // Ruta numerica de wayfinding del estilo /123/45/tienda/6/7.
          // Tambien la maneja WayfindingComponent con su propio page_view.
          if (/^\/\d+\/\d+\/[^/]+\/\d+\/\d+$/.test(url)) {
            return;
          }
        }

        // OJO: no pasamos document.title. Angular no actualiza el <title>
        // por ruta, asi que siempre vale "EU Antofagasta+" y ensuciaba
        // todos los page_views en GA4. Dejando title undefined hacemos
        // que resolveTitle() use el mapa por ruta.
        this.sendPageView(url);
      });
  }

  /**
   * Devuelve un session_id estable por pestaña.
   * Útil para unir eventos de un mismo flujo de wayfinding en GA4.
   */
  private ensureSessionId(): string {
    try {
      const key = 'eu_session_id';
      let id = sessionStorage.getItem(key);
      if (!id) {
        id = 't_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
        sessionStorage.setItem(key, id);
      }
      return id;
    } catch {
      return 't_' + Date.now();
    }
  }

  /**
   * Devuelve los parámetros base que se anexan a TODOS los eventos.
   * Tomados desde environment.ts para que cambien por totem/edificio sin recompilar lógica.
   */
  private baseParams(): BaseEventParams {
    const env: any = environment || {};
    return {
      totem_id: env.totem,
      building_id: env.edificio,
      channel: env.canal,
      language: env.idioma,
      session_id: this.sessionId
    };
  }

  /**
   * Llamada segura a gtag. Si no existe o estamos en modo debug sin id real,
   * solo hace console.debug para no romper el flujo.
   */
  private safeGtag(command: string, action: string, params?: any): void {
    const env: any = environment || {};
    const debug = !!env.analyticsDebug;

    if (debug) {
      // eslint-disable-next-line no-console
      console.debug('[GA4]', command, action, params);
    }

    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }

    try {
      window.gtag(command, action, params);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[GA4] error enviando evento', action, err);
    }
  }

  private enrich<T>(params: T): T & BaseEventParams {
    return { ...this.baseParams(), ...(params as any) };
  }

  // ---------------------------------------------------------------------------
  // Consent Mode v2
  // ---------------------------------------------------------------------------

  /**
   * Actualiza Consent Mode. Llamar desde un banner de cookies si existe.
   * Por defecto el index.html puede inicializar todo en "denied" y este método
   * lo eleva a "granted" cuando el usuario acepta.
   */
  updateConsent(granted: boolean): void {
    this.safeGtag('consent', 'update', {
      ad_storage: granted ? 'granted' : 'denied',
      analytics_storage: granted ? 'granted' : 'denied',
      ad_user_data: granted ? 'granted' : 'denied',
      ad_personalization: granted ? 'granted' : 'denied'
    });
  }

  // ---------------------------------------------------------------------------
  // Page views (SPA)
  // ---------------------------------------------------------------------------

  /**
   * Mapa de rutas -> titulo humano. Se usa para que GA4 nunca muestre
   * `(not set)` en page_title cuando `document.title` todavia no se
   * actualizo al momento del NavigationEnd (caso tipico: home al arrancar).
   */
  private readonly titlesByPath: { [path: string]: string } = {
    '/': 'Home',
    '/home': 'Home',
    '/buscar/tiendas': 'Busqueda de tiendas',
    '/buscar/servicios': 'Busqueda de servicios',
    '/buscar/eventos': 'Busqueda de eventos',
    '/tiendas': 'Directorio de tiendas',
    '/estacionamientos': 'Estacionamientos',
    '/informaciones': 'Informaciones',
    '/donde-quiero-ir': 'A donde quiero ir',
    '/ofertas': 'Ofertas',
    '/areas': 'Areas',
    '/noqr': 'QR invalido',
  };

  /**
   * Resuelve un titulo amigable para la ruta dada. Si el caller ya pasa un
   * titulo valido lo respetamos; si no, intentamos el mapa estatico; como
   * ultimo recurso caemos al titulo del documento o al path en bruto (nunca
   * string vacio, para que GA4 no lo pinte como "not set").
   */
  private resolveTitle(path: string, explicitTitle?: string): string {
    // Titulos considerados "genericos" (el <title> de index.html) — los
    // ignoramos para que no tapen el mapa por ruta en GA4.
    const genericTitles = ['EU Antofagasta+', 'totem-front-base'];
    const titleOk = !!(explicitTitle
      && explicitTitle.trim().length
      && genericTitles.indexOf(explicitTitle.trim()) === -1);
    if (titleOk) {
      return explicitTitle.trim();
    }
    const normalized = (path || '/').split('?')[0].split(';')[0];
    if (this.titlesByPath[normalized]) {
      return this.titlesByPath[normalized];
    }
    // Rutas dinamicas frecuentes
    if (normalized.startsWith('/tienda-detalle/')) return 'Detalle de tienda';
    if (normalized.startsWith('/evento-detalle/')) return 'Detalle de evento';
    if (normalized.startsWith('/qrcode/')) return 'Entrada por QR';
    if (normalized.startsWith('/wayfinding/') || normalized.startsWith('/mapa/')) {
      return 'Mapa / ruta';
    }
    const docTitle = (typeof document !== 'undefined' && document.title) ? document.title : '';
    return docTitle || normalized || 'Home';
  }

  sendPageView(path: string, title?: string, extra?: Record<string, any>): void {
    const normalizedPath = path && path.length ? path : '/home';
    const safePath = normalizedPath === '/' ? '/home' : normalizedPath;
    const safeTitle = this.resolveTitle(safePath, title);
    this.safeGtag('event', 'page_view', this.enrich({
      page_path: safePath,
      page_title: safeTitle,
      page_location: typeof window !== 'undefined' ? window.location.href : safePath,
      ...(extra || {})
    }));
  }

  // ---------------------------------------------------------------------------
  // Eventos clave del wayfinding
  // ---------------------------------------------------------------------------

  /**
   * Tracking de búsquedas dentro del buscador.
   * Registra término, categoría, cuántos resultados dio y si fue "zero results".
   * Zero-results es el indicador clave para detectar gaps del catálogo.
   */
  trackSearch(params: SearchStoreParams): void {
    // GA4 tiene un evento recomendado 'search' con 'search_term'.
    // Lo usamos además del custom 'search_store' para poder usar reportes nativos.
    this.safeGtag('event', 'search', this.enrich({
      search_term: params.search_term,
      search_category: params.search_category,
      results_count: params.results_count,
      zero_results: params.zero_results
    }));
    this.safeGtag('event', 'search_store', this.enrich(params));
  }

  /**
   * Tracking de ruta generada (origen → destino) en el wayfinding.
   * Este es el evento más importante para medir utilidad real del tótem.
   */
  trackRouteGenerated(params: RouteGeneratedParams): void {
    this.safeGtag('event', 'route_generated', this.enrich(params));
    // Se mantiene el evento legacy 'route_start' mientras se migran dashboards.
    this.safeGtag('event', 'route_start', this.enrich({
      origin_totem: params.origin_totem,
      destination_type: params.destination_type,
      destination_name: params.destination_name
    }));
  }

  /**
   * Tracking cuando el usuario envía la ruta a su móvil (QR o link).
   */
  trackQrSendToMobile(params: QrSendMobileParams): void {
    this.safeGtag('event', 'qr_send_to_mobile', this.enrich(params));
  }

  /**
   * Evento de escaneo de QR físico que lleva al usuario al mapa.
   *
   * Normaliza los nombres de campo: acepta tanto el schema viejo (qr_name,
   * code) como el nuevo (totem_name, totem_id, qr_code) y los emite con
   * nombres canonicos a GA4.
   */
  trackQrScanEntry(params: QrScanEntryParams): void {
    const canonical: any = {
      qr_name: params.qr_name || params.totem_name,
      totem_name: params.totem_name || params.qr_name,
      totem_id: params.totem_id,
      floor: params.floor,
      code: params.code || params.qr_code,
      qr_code: params.qr_code || params.code,
    };
    this.safeGtag('event', 'qr_scan_entry', this.enrich(canonical));
    // Compat con nombre anterior.
    this.safeGtag('event', 'qr_entry', this.enrich(canonical));
  }

  /**
   * Inactividad / abandono: cuando el totem cierra la sesion por idle.
   */
  trackIdleTimeout(screen: string, durationSec: number): void {
    this.safeGtag('event', 'idle_timeout', this.enrich({
      screen,
      duration_sec: durationSec
    }));
  }

  // ---------------------------------------------------------------------------
  // Compatibilidad con llamadas antiguas (mantener temporalmente)
  // ---------------------------------------------------------------------------

  sendViewItem(itemId: any, itemName: string): void {
    this.safeGtag('event', 'view_item', this.enrich({
      item_id: itemId,
      item_name: itemName
    }));
  }

  sendServiceView(serviceName: string, floor?: any, poi?: any): void {
    this.safeGtag('event', 'service_view', this.enrich({
      service_name: serviceName,
      floor: floor || null,
      poi_id: poi || null
    }));
  }

  /**
   * Evento dedicado para estacionamientos. Se emite ademas de (o en lugar de)
   * sendServiceView, para que en GA4 los reportes separen "estacionamientos"
   * de los servicios genericos (banos, ascensores, etc.).
   */
  sendParkingView(parkingName: string, floor?: any, poi?: any): void {
    this.safeGtag('event', 'parking_view', this.enrich({
      parking_name: parkingName,
      floor: floor || null,
      poi_id: poi || null
    }));
  }

  sendSectionView(sectionName: string): void {
    this.safeGtag('event', 'section_view', this.enrich({ section_name: sectionName }));
  }

  sendEventView(eventName: string, eventCategory?: string, eventId?: any): void {
    this.safeGtag('event', 'event_view', this.enrich({
      event_name_friendly: eventName,
      event_category: eventCategory || 'eventos',
      event_id: eventId || null
    }));
  }

  /**
   * Alias legacy. Preferir trackRouteGenerated en codigo nuevo.
   */
  sendRouteStart(origin: string, type: string, destination: string): void {
    this.trackRouteGenerated({
      origin_totem: origin,
      origin_type: 'Totem',
      destination_id: '',
      destination_name: destination,
      destination_type: type
    });
  }

  /**
   * Alias legacy. Preferir trackQrScanEntry en codigo nuevo.
   */
  sendQrEntry(qrName: string, floor: string, code: string): void {
    this.trackQrScanEntry({ qr_name: qrName, floor, code });
  }
}
